import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useToastStore } from '../../../stores/ToastStore';

/**
 * Hook that manages user-message inline editing.
 *
 * @param messageContent  The current text of the message being edited.
 * @param messageIndex    Index of the message in the chat history.
 * @param onEditMessage   Callback to persist the edit (from parent).
 */
export function useMessageEdit(
	messageContent: string,
	messageIndex: number,
	onEditMessage?: (index: number, newContent: string) => void,
) {
	const { showToast } = useToastStore();

	const [isEditing, setIsEditing] = useState(false);
	const [editedContent, setEditedContent] = useState(messageContent);
	const editTextareaRef = useRef<HTMLTextAreaElement>(null);

	const handleEdit = useCallback(() => {
		setIsEditing(true);
		setEditedContent(messageContent);
	}, [messageContent]);

	const handleSaveEdit = useCallback(() => {
		if (
			editedContent.trim() &&
			onEditMessage &&
			messageIndex !== undefined
		) {
			onEditMessage(messageIndex, editedContent.trim());
			setIsEditing(false);
		} else {
			showToast('Message cannot be empty.', 'warning', {
				timeout: 3000,
				placement: 'chat',
			});
		}
	}, [editedContent, onEditMessage, messageIndex, showToast]);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setEditedContent(messageContent);
	}, [messageContent]);

	const handleEditKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				handleSaveEdit();
			} else if (e.key === 'Escape') {
				handleCancelEdit();
			}
		},
		[handleSaveEdit, handleCancelEdit],
	);

	// Focus + move cursor to end when entering edit mode
	useEffect(() => {
		if (isEditing && editTextareaRef.current) {
			editTextareaRef.current.focus();
			editTextareaRef.current.setSelectionRange(
				editTextareaRef.current.value.length,
				editTextareaRef.current.value.length,
			);
		}
	}, [isEditing]);

	// Auto-resize textarea based on content
	useEffect(() => {
		if (isEditing && editTextareaRef.current) {
			const textarea = editTextareaRef.current;
			textarea.style.height = 'auto';
			// Set height based on content, with min and max constraints
			const newHeight = Math.min(
				Math.max(textarea.scrollHeight, 50),
				400,
			);
			textarea.style.height = `${newHeight}px`;
		}
	}, [isEditing, editedContent]);

	return {
		isEditing,
		editedContent,
		setEditedContent,
		editTextareaRef,
		handleEdit,
		handleSaveEdit,
		handleCancelEdit,
		handleEditKeyDown,
	};
}
